import { useCallback, useEffect, useRef, useState } from 'react'

const PREPARING_HINT_AFTER_MS = 12_000 // 12 s
const PREPARING_REFETCH_INTERVAL_MS = 5_000 // 5 s
const PREPARING_MAX_AUTO_REFETCHES = 6 // 6 interval retries
const MAX_AUTO_CREATE_ATTEMPTS = 3 // 3 creation attempts

interface FloatWatchdogParams {
  isCallOwnedByThisTab: boolean
  hasScope: boolean
  roomUrl?: string
  // The call the room is for; a new one starts the watch again.
  scopeKey?: string
  isEnsuringRoom: boolean
  ensureRoom: () => void
  refetchRoom: () => void
}

export interface FloatWatchdogState {
  isPreparingRoom: boolean
  isPreparingTooLong: boolean
  retryRoom: () => void
}

// Recovers room creation only; the Whereby SDK owns connection recovery for an initialized room.
export const useGlobalWherebyFloatWatchdogs = ({
  isCallOwnedByThisTab,
  hasScope,
  roomUrl,
  scopeKey,
  isEnsuringRoom,
  ensureRoom,
  refetchRoom
}: FloatWatchdogParams): FloatWatchdogState => {
  const [hasRequestedRoom, setHasRequestedRoom] = useState(false)
  const [isPreparingTooLong, setIsPreparingTooLong] = useState(false)
  const createAttemptsRef = useRef(0)
  const ensureRoomRef = useRef(ensureRoom)
  const isEnsuringRoomRef = useRef(isEnsuringRoom)

  ensureRoomRef.current = ensureRoom
  isEnsuringRoomRef.current = isEnsuringRoom

  const isPreparingRoom = isCallOwnedByThisTab && hasScope && !roomUrl

  useEffect(() => {
    setHasRequestedRoom(false)
    setIsPreparingTooLong(false)
    createAttemptsRef.current = 0
  }, [scopeKey])

  useEffect(() => {
    if (!isCallOwnedByThisTab || !hasScope || roomUrl || hasRequestedRoom || isEnsuringRoom) return
    if (createAttemptsRef.current >= MAX_AUTO_CREATE_ATTEMPTS) return

    setHasRequestedRoom(true)
    createAttemptsRef.current += 1
    ensureRoomRef.current()
  }, [hasRequestedRoom, hasScope, isCallOwnedByThisTab, isEnsuringRoom, roomUrl])

  useEffect(() => {
    if (!isPreparingRoom) {
      setIsPreparingTooLong(false)
      return
    }

    const hintTimer = window.setTimeout(() => setIsPreparingTooLong(true), PREPARING_HINT_AFTER_MS)
    let attempts = 0
    const retryTimer = window.setInterval(() => {
      attempts += 1
      if (attempts > PREPARING_MAX_AUTO_REFETCHES) {
        window.clearInterval(retryTimer)
        return
      }
      if (!isEnsuringRoomRef.current) ensureRoomRef.current()
    }, PREPARING_REFETCH_INTERVAL_MS)

    return () => {
      window.clearTimeout(hintTimer)
      window.clearInterval(retryTimer)
    }
  }, [isPreparingRoom])

  const retryRoom = useCallback(() => {
    createAttemptsRef.current = 0
    setHasRequestedRoom(false)
    setIsPreparingTooLong(false)
    refetchRoom()
  }, [refetchRoom])

  return { isPreparingRoom, isPreparingTooLong, retryRoom }
}
